/**
 * Inline onboarding ribbon. Slim 3-step strip ("Import your data → See
 * your queue → Make your first touch") that tracks progress in
 * localStorage and hides once all three are complete or dismissed.
 *
 * Renders alongside OnboardingWizard for new operators whose
 * `account.activation.onboardingWizardCompleted: false` would otherwise
 * be suppressed by the wizard's stricter shouldShow gate.
 */

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { useAppState, useSession } from "@/lib/queries";
import { cn } from "@/lib/cn";

const STEP_KEYS = ["data", "queue", "touch"] as const;
type StepKey = (typeof STEP_KEYS)[number];

interface Step {
  key: StepKey;
  label: string;
  cta: string;
  to: string;
}

const STEPS: Step[] = [
  {
    key: "data",
    label: "Import your data",
    cta: "Open Imports",
    to: "/platform/imports",
  },
  {
    key: "queue",
    label: "See your workspace",
    cta: "Open Workspace",
    to: "/operations",
  },
  {
    key: "touch",
    label: "Make your first touch",
    cta: "Take action",
    to: "/operations",
  },
];

interface AppShape {
  account?: {
    activation?: {
      onboardingWizardCompleted?: boolean;
      lifecycleStage?: string;
    };
    isSandbox?: boolean;
  };
  summary?: {
    dispatchesSent?: number;
  };
  uiAccess?: {
    isGlobalOperator?: boolean;
  };
}

interface SessionShape {
  user?: { id?: string };
}

function dismissKeyFor(userId: string): string {
  return `rc_onboarding_ribbon_dismissed_${userId || "anon"}`;
}

function stepKeyFor(userId: string, step: StepKey): string {
  return `rc_onboarding_step_${userId || "anon"}_${step}`;
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    if (value) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {
    /* localStorage may be blocked — server-side state is authoritative */
  }
}

/**
 * Mark a ribbon step done from outside this component. Used by
 * WorkspacePage after a successful outcome record / compose send so
 * "Make your first touch" auto-progresses without the operator having
 * to click the ribbon link first.
 */
export function markOnboardingStepDone(
  userId: string | undefined,
  step: "data" | "queue" | "touch",
): void {
  writeFlag(stepKeyFor(userId ?? "", step), true);
}

export function OnboardingRibbon() {
  const { data: appRaw } = useAppState();
  const { data: sessionRaw } = useSession();
  const app = (appRaw ?? {}) as AppShape;
  const session = (sessionRaw ?? {}) as SessionShape;
  const userId = session.user?.id ?? "";

  const [dismissed, setDismissed] = useState(() =>
    readFlag(dismissKeyFor(userId)),
  );
  const [progressNonce, setProgressNonce] = useState(0);
  const location = useLocation();

  // Auto-progress the "queue" step the first time the operator lands on
  // /operations — clicking the ribbon link is one trigger, but most
  // users navigate via the sidebar or browser back/forward, in which
  // case handleStepClick never fires and the ribbon stays at 0/3 even
  // after they've clearly seen the workspace.
  useEffect(() => {
    if (!userId) return;
    if (location.pathname === "/operations") {
      const key = stepKeyFor(userId, "queue");
      if (!readFlag(key)) {
        writeFlag(key, true);
        setProgressNonce((n) => n + 1);
      }
    }
  }, [location.pathname, userId]);

  // Auto-progress the "touch" step the first time any dispatch fires —
  // operator-initiated touches already mark this step done from
  // WorkspacePage's outcome record, but the brand promise is "the system
  // does it for you," so a system-initiated cadence touch must also tick
  // the box. summary.dispatchesSent counts every dispatchQueue row with
  // status === "sent" or a sentAt timestamp; any nonzero value means
  // recovery work has happened on this account (redteam 2026-05-05 §1
  // P1 #9).
  const dispatchesSent = app.summary?.dispatchesSent ?? 0;
  useEffect(() => {
    if (!userId) return;
    if (dispatchesSent > 0) {
      const key = stepKeyFor(userId, "touch");
      if (!readFlag(key)) {
        writeFlag(key, true);
        setProgressNonce((n) => n + 1);
      }
    }
  }, [dispatchesSent, userId]);

  const completed = app.account?.activation?.onboardingWizardCompleted === true;
  const isInternal = app.account?.activation?.lifecycleStage === "internal";
  const isGlobalOperator = app.uiAccess?.isGlobalOperator === true;
  // Previously suppressed in sandbox to avoid contradicting seeded fixtures
  // ("Import your data — not done" against a pre-populated demo account).
  // Audit feedback: sandbox owners are exactly the cohort that needs the
  // ribbon — they're the ones evaluating whether to flip to live. The
  // ribbon now renders in sandbox; steps already reflect actual milestone
  // state (data_source_connected, twilio configured, cadences, team) so
  // a fresh sandbox shows accurate "0 of 4" progress on real work.
  if (completed || dismissed || isInternal || isGlobalOperator) return null;

  const isStepDone = (k: StepKey): boolean =>
    readFlag(stepKeyFor(userId, k));

  // intentionally read once per render (progressNonce forces re-evaluation
  // when a step is marked done so the UI updates)
  void progressNonce;
  const doneCount = STEPS.filter((s) => isStepDone(s.key)).length;
  if (doneCount === STEPS.length) return null;

  const handleStepClick = (k: StepKey) => {
    writeFlag(stepKeyFor(userId, k), true);
    setProgressNonce((n) => n + 1);
  };

  const handleDismiss = () => {
    writeFlag(dismissKeyFor(userId), true);
    setDismissed(true);
  };

  return (
    <div className="border-b border-dw-border bg-dw-blue/5">
      <div className="px-dw-4 py-dw-2 flex items-center gap-dw-3 flex-wrap">
        <span className="text-xs uppercase tracking-wide text-dw-text-3 font-medium shrink-0">
          Get started · {doneCount} of {STEPS.length}
        </span>
        <ol className="flex items-center gap-dw-2 flex-wrap">
          {STEPS.map((step) => {
            const done = isStepDone(step.key);
            return (
              <li key={step.key}>
                <Link
                  to={step.to}
                  onClick={() => handleStepClick(step.key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2 py-1 rounded-dw-sm text-xs",
                    done
                      ? "text-dw-text-3 line-through"
                      : "text-dw-blue hover:bg-dw-blue/10",
                  )}
                  aria-label={`${step.label} — ${done ? "done" : "not done"}`}
                >
                  <span aria-hidden="true">{done ? "✓" : "○"}</span>
                  <span>{step.label}</span>
                </Link>
              </li>
            );
          })}
        </ol>
        <button
          type="button"
          onClick={handleDismiss}
          className="ml-auto text-xs text-dw-text-3 hover:underline shrink-0"
          aria-label="Hide onboarding checklist"
        >
          Hide
        </button>
      </div>
    </div>
  );
}
